import React from "react";
import { FallbackProps } from "react-error-boundary";
import { Box, Button, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import HomeIcon from "@mui/icons-material/Home";
import RefreshIcon from "@mui/icons-material/Refresh";

const ErrorBoundaryFallback: React.FC<FallbackProps> = ({ error }) => {
  const err = error instanceof Error ? error : null;
  const isOffline = !navigator.onLine;

  const isChunkError =
    isOffline ||
    err?.name === "ChunkLoadError" ||
    err?.message?.toLowerCase().includes("loading chunk") ||
    err?.message?.toLowerCase().includes("chunkloaderror") ||
    err?.message
      ?.toLowerCase()
      .includes("failed to fetch dynamically imported module") ||
    err?.message?.toLowerCase().includes("failed to fetch") ||
    err?.message?.toLowerCase().includes("load failed") ||
    err?.message?.toLowerCase().includes("networkerror");

  const handleClick = () => {
    if (isChunkError) {
      window.location.reload();
    } else {
      window.location.href = "/";
    }
  };

  // Title / description / icon based on error type
  const title = isOffline ? "No Internet Connection" : "Something Went Wrong";

  const description = isOffline
    ? "You appear to be offline. Please check your network connection and try again."
    : "Something went wrong. Please try again.";

  const Icon = isOffline ? WifiOffIcon : ErrorOutlineIcon;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        px: 2,
        textAlign: "center",
        bgcolor: "background.default",
      }}
    >
      {/* Icon */}
      <Icon
        sx={{
          fontSize: { xs: 64, sm: 80 },
          color: isOffline ? "warning.main" : "error.main",
          mb: 2,
        }}
      />

      {/* Title */}
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          fontSize: { xs: "1.5rem", sm: "2rem" },
          fontWeight: 600,
          color: "text.primary",
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 4,
          maxWidth: 400,
          fontSize: { xs: "0.875rem", sm: "1rem" },
        }}
      >
        {description}
      </Typography>

      {/* Button */}
      <Button
        variant="contained"
        size="large"
        onClick={handleClick}
        startIcon={isChunkError ? <RefreshIcon /> : <HomeIcon />}
        sx={{
          minWidth: { xs: "100%", sm: 200 },
          maxWidth: 300,
          textTransform: "none",
          fontSize: "1rem",
          py: 1.5,
          bgcolor: isOffline ? "warning.main" : undefined,
          "&:hover": isOffline ? { bgcolor: "warning.dark" } : undefined,
        }}
      >
        {isChunkError ? "Retry / Reload" : "Go to Homepage"}
      </Button>

      {/* Error details (Development only) */}
      {process.env.NODE_ENV === "development" && err?.message && (
        <Box
          sx={{
            mt: 4,
            p: 2,
            bgcolor: "warning.lighter",
            borderRadius: 1,
            maxWidth: 600,
            width: "100%",
            textAlign: "left",
          }}
        >
          <Typography
            variant="caption"
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.75rem",
              color: "warning.dark",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              m: 0,
            }}
          >
            {err.message}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ErrorBoundaryFallback;
